import { Form, Formik } from "formik";
import * as yup from "yup";
import { FormikInput } from "@/components/common/form/FormikInput.tsx";
import { Button } from "@/components/base/Button.tsx";
import { Autocomplete } from "@/components/base/Autocomplete.tsx";
import { useEffect, useMemo, useState } from "react";
import { Option } from "@/types/Option.ts";
import { generator as generatorActions } from "@/repositories/generator.ts";
import { customer as customerActions } from "@/repositories/customer.ts";
import { technician as technicianActions } from "@/repositories/technician.ts";
import { LoadingPage } from "@/components/page/LoadingPage.tsx";
import { Label } from "@/components/base/Label.tsx";
import {
  Appointment,
  CreateAppointmentDto,
  PatchAppointmentDto,
} from "@/models";
import { FormikDateInput } from "@/components/common/form/FormikDateInput.tsx";
import { FormikTextarea } from "@/components/common/form/FormikTextarea.tsx";
import { FormikSelect } from "@/components/common/form/FomikSelect.tsx";
import { useStore } from "@/stores/useStore.ts";
import { DEFAULT_SERVICE_OPTIONS } from "@/lib/const.ts";
import { FormikSwitch } from "@/components/common/form/FormikSwitch.tsx";
import { Checkbox } from "@/components/base/Checkbox.tsx";
import { firestoreToDateTime } from "@/lib/utils.ts";

type CreateEditAppointmentFormProps = {
  onSubmit: (
    values: CreateAppointmentDto | PatchAppointmentDto
  ) => Promise<void>;
  initialAppointmentData?: Appointment;
  onCancel?: () => void;
  tenantId: string;
  isEditable?: boolean;
};

const validationSchema = yup.object({
  generatorId: yup.string().required("Generator is required"),
  customerId: yup.string().required("Customer is required"),
  desiredDate: yup.date().required("Desired appointment date is required"),
});

export const CreateEditAppointmentForm = ({
  initialAppointmentData,
  onCancel,
  onSubmit,
  tenantId,
  isEditable,
}: CreateEditAppointmentFormProps) => {
  const tenant = useStore((s) => s.tenant);
  const serviceOptions = useMemo(
    () => tenant?.settings?.serviceOptions ?? DEFAULT_SERVICE_OPTIONS,
    [tenant]
  );
  const [availableCustomers, setAvailableCustomers] =
    useState<{ option: Option; id: string; generatorId: string }[]>();

  const [availableGenerators, setAvailableGenerators] =
    useState<{ option: Option; id: string; customerId: string }[]>();

  const [availableTechnicians, setAvailableTechnicians] =
    useState<{ option: Option; id: string; technicianId: string }[]>();

  const [isLoading, setIsloading] = useState<boolean>(false);

  useEffect(() => {
    setIsloading(true);
    generatorActions
      .getOptions(tenantId)
      .then((result) =>
        setAvailableGenerators(
          result.index
            .filter((item) => item.customerId !== "")
            .map((item) => ({
              option: {
                value: `${item.make} ${item.modelNumber}: ${item.serialNumber}`,
                label: `${item.make} ${item.modelNumber}: ${item.serialNumber}`,
              },
              id: item.id,
              customerId: item.customerId,
            }))
        )
      )
      .then(() =>
        customerActions.getOptions(tenantId).then((result) =>
          setAvailableCustomers(
            result.index.map((item) => ({
              option: {
                value: `${item.firstName} ${item.lastName}`,
                label: `${item.firstName} ${item.lastName}`,
              },
              id: item.id,
              generatorId: item.generatorId,
            }))
          )
        )
      )
      .then(() =>
        technicianActions.getOptions(tenantId).then((result) =>
          setAvailableTechnicians(
            result.index.map((item) => ({
              option: {
                value: `${item.firstName} ${item.lastName}`,
                label: `${item.firstName} ${item.lastName}`,
              },
              id: item.id,
              technicianId: item.id,
            }))
          )
        )
      )
      .finally(() => setIsloading(false));
  }, []);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const findGeneratorId = (value: string) => {
    return availableGenerators?.find((item) => item.option.value === value)?.id;
  };

  const findGeneratorSearchTerm = (id: string) => {
    const generator = availableGenerators?.find((item) => item.id === id);
    if (!generator) {
      return "";
    }
    return generator.option.value;
  };

  const customerFromGenerator = (value: string) => {
    return availableGenerators?.find((item) => item.option.value === value)
      ?.customerId;
  };

  const findCustomerId = (value: string) => {
    return availableCustomers?.find((item) => item.option.value === value)?.id;
  };

  const findCustomerSearchTerm = (id: string) => {
    const customer = availableCustomers?.find((item) => item.id === id);
    if (!customer) {
      return "";
    }
    return customer.option.value;
  };

  const findTechnicianId = (value: string) => {
    return availableTechnicians?.find((item) => item.option.value === value)
      ?.id;
  };

  const findTechnicianSearchTerm = (id: string) => {
    const technician = availableTechnicians?.find((item) => item.id === id);
    if (!technician) {
      return "";
    }
    return technician.option.value;
  };

  const generatorFromCustomer = (value: string) => {
    return availableCustomers?.find((item) => item.option.value === value)
      ?.generatorId;
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <Formik
      initialValues={{
        generatorSearchTerm: initialAppointmentData?.generatorId
          ? findGeneratorSearchTerm(initialAppointmentData?.generatorId)
          : "",
        customerSearchTerm: initialAppointmentData?.customerId
          ? findCustomerSearchTerm(initialAppointmentData?.customerId)
          : "",
        technicianSearchTerm: initialAppointmentData?.technicianId
          ? findTechnicianSearchTerm(initialAppointmentData?.technicianId)
          : "",
        customerId: initialAppointmentData?.customerId ?? "",
        generatorId: initialAppointmentData?.generatorId ?? "",
        technicianId: initialAppointmentData?.technicianId ?? "",
        desiredDate:
          firestoreToDateTime(initialAppointmentData?.desiredDate) ??
          new Date(),
        notes: initialAppointmentData?.notes ?? "",
        automaticallyReschedule:
          initialAppointmentData?.automaticallyReschedule ?? false,
        status: initialAppointmentData?.status ?? "scheduled",
        partsUsed: initialAppointmentData?.partsUsed ?? "",
        serviceType: initialAppointmentData?.serviceType ?? "",
      }}
      validateOnMount={true}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        let submitData: CreateAppointmentDto | PatchAppointmentDto = {
          tenantId: tenantId,
          generatorId: values.generatorId,
          customerId: values.customerId,
          desiredDate: values.desiredDate,
          technicianId: values.technicianId,
          notes: values.notes,
          status: values.status,
          automaticallyReschedule: values.automaticallyReschedule,
          partsUsed: values.partsUsed,
          serviceType: values.serviceType,
        } as CreateAppointmentDto;

        if (initialAppointmentData?.id) {
          submitData = {
            id: initialAppointmentData.id,
            ...submitData,
          } as PatchAppointmentDto;
        }

        onSubmit(submitData).finally(() => setSubmitting(false));
        return;
      }}
    >
      {({ values, setFieldValue, handleSubmit }) => (
        <Form
          className="flex flex-col sm:grid sm:grid-cols-2 gap-4 m-auto w-64 sm:w-full max-w-screen-md"
          onSubmit={handleSubmit}
        >
          <div className="flex col-span-2 sm:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Customer Information</h3>
            <div className="flex flex-col gap-2">
              <Label htmlFor={"customerSearchTerm"}>Customer</Label>
              <Autocomplete
                autocompleteOptions={
                  availableCustomers?.map((item) => item.option) ?? []
                }
                value={values.customerSearchTerm}
                setValue={(value) => {
                  const generatorId = generatorFromCustomer(value);
                  const generatorSearchTerm = generatorId
                    ? findGeneratorSearchTerm(generatorId)
                    : "";
                  setFieldValue("customerId", findCustomerId(value));
                  setFieldValue("customerSearchTerm", value);
                  setFieldValue("generatorId", generatorId);
                  setFieldValue("generatorSearchTerm", generatorSearchTerm);
                }}
                id="customerSearchTerm"
                disabled={!isEditable}
              />
            </div>
            <FormikInput
              name="customerId"
              type="text"
              id="customerId"
              label="Customer ID"
              disabled={true}
            />
            <FormikDateInput
              name="desiredDate"
              type="date"
              id="desiredDate"
              label="Desired Appointment Date"
              disabled={!isEditable}
            />
          </div>
          <div className="flex col-span-2 sm:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Generator Information</h3>
            <div className="flex flex-col gap-2">
              <Label htmlFor={"generatorSearchTerm"}>Generator</Label>
              <Autocomplete
                autocompleteOptions={
                  availableGenerators?.map((item) => item.option) ?? []
                }
                value={values.generatorSearchTerm}
                setValue={(value) => {
                  const customerId = customerFromGenerator(value);
                  const customerSearchTerm = customerId
                    ? findCustomerSearchTerm(customerId)
                    : "";
                  setFieldValue("generatorId", findGeneratorId(value));
                  setFieldValue("generatorSearchTerm", value);
                  setFieldValue("customerId", customerId);
                  setFieldValue("customerSearchTerm", customerSearchTerm);
                }}
                id="generatorSeachTerm"
                disabled={!isEditable}
              />
            </div>
            <FormikInput
              name="generatorId"
              type="text"
              id="generatorId"
              label="Generator ID"
              disabled={true}
            />
          </div>
          <div className="flex col-span-2 sm:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Technician Information</h3>
            <div className="flex flex-col gap-2">
              <Label htmlFor={"technicianSearchTerm"}>Technician</Label>
              <Autocomplete
                autocompleteOptions={
                  availableTechnicians?.map((item) => item.option) ?? []
                }
                value={values.technicianSearchTerm}
                setValue={(value) => {
                  setFieldValue("technicianId", findTechnicianId(value));
                  setFieldValue("technicianSearchTerm", value);
                }}
                id="technicianSearchTerm"
                disabled={!isEditable}
              />
            </div>
            <FormikInput
              name="technicianId"
              type="text"
              id="technicianId"
              label="Technician ID"
              disabled={true}
            />
          </div>
          <div className="flex col-span-2 sm:col-span-1 flex-col gap-2 border p-2">
            <h3 className="text-lg">Notes</h3>
            <FormikTextarea
              name="notes"
              id="notes"
              label="Notes"
              disabled={!isEditable}
            />
          </div>
          <div className="flex col-span-2 sm:col-span-2 flex-col gap-2 border p-2">
            <h3 className="text-lg">Service Information</h3>
            <div className="flex flex-col sm:grid sm:grid-cols-2 gap-2">
              <FormikSelect
                disabled={!isEditable}
                name="serviceType"
                id="serviceType"
                label="Service Type"
                options={serviceOptions}
              />
              <div className="flex flex-col gap-6">
                <div className={"align-middle flex flex-row gap-4 "}>
                  <Label htmlFor={"status"}>Completed</Label>
                  <Checkbox
                    disabled={!isEditable}
                    checked={values.status === "completed"}
                    onCheckedChange={(checked) =>
                      setFieldValue(
                        "status",
                        checked ? "completed" : "inprogress"
                      )
                    }
                  />
                </div>
                <FormikSwitch
                  disabled={!isEditable}
                  name="automaticallyReschedule"
                  id="automaticallyReschedule"
                  label="Automatically Reschedule"
                />
              </div>
            </div>
            <FormikTextarea
              disabled={!isEditable}
              name="partsUsed"
              id="partsUsed"
              label="Parts Used"
            />
          </div>
          {isEditable && (
            <div className="flex col-span-2 flex-row gap-4 justify-end">
              <Button
                variant={"secondary"}
                onClick={handleCancel}
                type="button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={values.generatorId === "" && values.customerId === ""}
              >
                Save
              </Button>
            </div>
          )}
        </Form>
      )}
    </Formik>
  );
};
